/**
 * The 15-minute idle lock's clock — design decision 7 (`BACKOFFICE-FRONTEND-DESIGN.md`).
 *
 * Pure bookkeeping: when did this origin last see a human, and has that been too long. It knows
 * nothing about the router or the DOM; `app/idle-guard.ts` wires it to real events and ends the
 * session, and `rehydrate.ts` consults it before trusting a token it finds on disk.
 *
 * **Why the timestamp is persisted, not held in memory alone.** The token lives in `localStorage`
 * so a reload does not bounce a manager mid-count to the login screen. Without a persisted
 * last-activity time the same property defeats the lock outright: walk away, come back an hour
 * later, press F5, and the stored token rehydrates as if nothing happened. Shared across tabs on
 * purpose, too — activity in one tab keeps the others alive, which is what "the person is still
 * here" means on one machine.
 *
 * **Missing timestamp = expired, when a token is on disk.** A token with no recorded activity
 * cannot prove it is recent, so rehydration refuses it (fail closed). `useAuth.login` stamps the
 * clock before it stores a token, so the only way to reach that state is a token written by a
 * build that predates this lock (one extra sign-in, once) or storage that refuses writes.
 *
 * **Not the design's full lock.** The design asks for a lock that keeps state and resumes with a
 * password. This drops the token and sends the operator to `/login` with a return path — the
 * route they were on comes back, unsaved form input does not. Every write is behind the server's
 * own confirmation anyway (never optimistic), so nothing half-committed is lost.
 */

export const IDLE_LIMIT_MS = 15 * 60_000;
export const LAST_ACTIVITY_STORAGE_KEY = "backoffice.lastActivity";

/**
 * Disk writes are throttled: `pointermove` fires dozens of times a second, and a write per event
 * is pointless churn that also fires a `storage` event in every other tab. The in-memory value is
 * always exact; disk lags it by at most this much, which is noise against fifteen minutes.
 */
const WRITE_THROTTLE_MS = 5_000;

let lastInMemory: number | null = null;
let lastWritten = 0;
let lockedOnLoad = false;

function readStored(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Records a human being present. `force` skips the write throttle (a login, a rehydration). */
export function markActive(now: number = Date.now(), force = false): void {
  lastInMemory = Math.max(lastInMemory ?? 0, now);
  if (!force && now - lastWritten < WRITE_THROTTLE_MS) return;
  lastWritten = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(lastInMemory));
  } catch {
    // Storage refused (Safari private mode, disabled storage). The in-memory clock still locks
    // this tab; a reload with storage refused has no token to rehydrate either.
  }
}

/** The latest activity this tab knows of — its own, or any other tab's via storage. */
export function lastActivity(): number | null {
  const stored = readStored();
  if (stored === null) return lastInMemory;
  if (lastInMemory === null) return stored;
  return Math.max(stored, lastInMemory);
}

/** True when there is no recorded activity, or the latest is at least `IDLE_LIMIT_MS` old. */
export function isIdleExpired(now: number = Date.now()): boolean {
  const last = lastActivity();
  return last === null || now - last >= IDLE_LIMIT_MS;
}

/** Forgets the clock — called when a session ends, so the next one starts from its own login. */
export function clearActivity(): void {
  lastInMemory = null;
  lastWritten = 0;
  try {
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch {
    // See `markActive`.
  }
}

/**
 * Set by `rehydrateSession` when it refused a stored token for idleness, read once by the router
 * guard so `/login` can say "locked due to inactivity" rather than showing a bare form.
 */
export function flagLockedOnLoad(): void {
  lockedOnLoad = true;
}

export function consumeLockedOnLoad(): boolean {
  const was = lockedOnLoad;
  lockedOnLoad = false;
  return was;
}
